import _ from 'lodash';
import { t } from '@lingui/macro';
import { BAD_INPUT_SENTINEL } from '../../form/NgpNativeNumberField';

/**
 * Mirrors portal/ui serviceTestThresholdConfig.directive.js showErrorMessage / validateDegraded / validateExcessive.
 * Checks degraded first, then excessive — returns the first error found.
 */
export function showThresholdError(threshold, degradedValue, criticalValue) {
    const degradedStr = String(degradedValue ?? '');
    const criticalStr = String(criticalValue ?? '');

    // --- validateDegraded ---
    const degradedError = (() => {
        if (degradedStr === '' || degradedValue == null) {
            return t`Degraded threshold required`;
        }
        if (degradedValue === BAD_INPUT_SENTINEL || _.isNaN(Number(degradedStr))) {
            return t`Thresholds must be numbers`;
        }
        const deg = parseFloat(degradedStr);
        if (!_.isNil(threshold.max) && deg > threshold.max) {
            return t`Degraded threshold exceeds maximum: ${threshold.max}`;
        }
        if (!_.isNil(threshold.min) && deg < threshold.min) {
            return t`Degraded threshold is less than minimum: ${threshold.min}`;
        }
        // ordering check (relative position between degraded and critical)
        if (criticalStr !== '' && criticalValue !== BAD_INPUT_SENTINEL && !_.isNaN(Number(criticalStr))) {
            const crit = parseFloat(criticalStr);
            if (threshold.inverted ? deg < crit : deg > crit) {
                return threshold.inverted
                    ? t`Values must follow: Excessive ≤ Degraded`
                    : t`Values must follow: Degraded ≤ Excessive`;
            }
        }
        return null;
    })();
    if (degradedError) return degradedError;

    // --- validateExcessive ---
    const excessiveError = (() => {
        if (criticalStr === '' || criticalValue == null) {
            return t`Excessive threshold required`;
        }
        if (criticalValue === BAD_INPUT_SENTINEL || _.isNaN(Number(criticalStr))) {
            return t`Thresholds must be numbers`;
        }
        const crit = parseFloat(criticalStr);
        if (!_.isNil(threshold.max) && crit > threshold.max) {
            return t`Excessive threshold exceeds maximum: ${threshold.max}`;
        }
        if (!_.isNil(threshold.min) && crit < threshold.min) {
            return t`Excessive threshold is less than minimum: ${threshold.min}`;
        }
        // ordering check (relative position between degraded and critical)
        if (degradedStr !== '' && degradedValue !== BAD_INPUT_SENTINEL && !_.isNaN(Number(degradedStr))) {
            const deg = parseFloat(degradedStr);
            if (threshold.inverted ? crit > deg : crit < deg) {
                return threshold.inverted
                    ? t`Values must follow: Excessive ≤ Degraded`
                    : t`Values must follow: Degraded ≤ Excessive`;
            }
        }
        return null;
    })();
    return excessiveError || null;
}

/**
 * Compute dynamic min/max for degraded/excessive inputs, mirroring portal/ui's getMax/getMin.
 */
export function getThresholdDynamicBounds(threshold, degradedValue, criticalValue) {
    const degradedNumber = parseFloat(degradedValue);
    const criticalNumber = parseFloat(criticalValue);
    const degradedOrMin = Number.isNaN(degradedNumber) ? threshold.min : degradedNumber;
    const criticalOrMax = Number.isNaN(criticalNumber) ? threshold.max : criticalNumber;

    const getMax = () => {
        if (threshold.inverted) {
            return threshold.max ? Math.min(threshold.max, degradedOrMin) : degradedOrMin;
        }
        return threshold.max ? Math.min(threshold.max, criticalOrMax) : criticalOrMax;
    };

    const getMin = () => {
        if (threshold.inverted) {
            return threshold.max ? Math.min(threshold.max, criticalOrMax) : criticalOrMax;
        }
        return threshold.min ? Math.max(threshold.min, degradedOrMin) : degradedOrMin;
    };

    return {
        degradedMin: threshold.inverted ? getMin() : threshold.min,
        degradedMax: threshold.inverted ? threshold.max : getMax(),
        excessiveMin: threshold.inverted ? threshold.min : getMin(),
        excessiveMax: threshold.inverted ? getMax() : threshold.max,
    };
}
